import { useEffect, useMemo, useState } from "react";
import {
  useStrategiesStore,
  getEffectiveSortField,
  type AssetClass,
  type Strategy,
} from "@/stores/strategiesStore";
import { useStrategies } from "@/hooks/useStrategies";
import { useOrganization } from "@/hooks/useOrganization";
import { StrategiesHeader } from "@/components/strategies/StrategiesHeader";
import { StrategiesFilterBar } from "@/components/strategies/StrategiesFilterBar";
import { StrategiesList } from "@/components/strategies/StrategiesList";
import { AllocationModal } from "@/components/strategies/AllocationModal";
import { ManageAllocationModal } from "@/components/strategies/ManageAllocationModal";
import { PerformanceDisclaimer } from "@/components/legal/PerformanceDisclaimer";
import { PortfolioBuilderCTA } from "@/components/portfolio-builder/PortfolioBuilderCTA";
import { trackEvent } from "@/lib/mixpanel";

export default function Strategies() {
  const [mounted, setMounted] = useState(false);

  // Fetch strategies from API
  const { strategies: apiStrategies, isLoading, error: apiError } = useStrategies();
  const { isViewOnly } = useOrganization();

  const store = useStrategiesStore();

  // Map API data and apply store filters/sort directly — no store round-trip
  const strategies = useMemo(() => {
    const mapped: Strategy[] = apiStrategies.map((s) => ({
      ...s,
      longDescription: s.longDescription ?? null,
      assetClasses: (s.assetClasses ?? []) as AssetClass[],
      supportedAssets: s.supportedAssets ?? [],
      sharpeRatio: s.sharpeRatio ?? 0,
      maxDrawdown: s.maxDrawdown ?? 0,
      winRate: s.winRate ?? 0,
      avgTradeDurationHours: s.avgTradeDurationHours ?? 0,
      launchDate: s.launchDate ? new Date(s.launchDate) : new Date(),
      userAllocation: null,
    }));

    let filtered = mapped;
    if (store.filters.marketView && store.filters.marketView !== "all") {
      filtered = filtered.filter((s) => s.marketView === store.filters.marketView);
    }
    if (store.filters.riskLevel && store.filters.riskLevel !== "all") {
      filtered = filtered.filter((s) => s.riskLevel === store.filters.riskLevel);
    }
    if (store.filters.assetClass && store.filters.assetClass !== "all") {
      filtered = filtered.filter((s) =>
        s.assetClasses?.includes(store.filters.assetClass as AssetClass)
      );
    }

    // Issue #131: if a hidden sort field (return/AUM) was persisted from a
    // prior session, fall back to "name" — shared with StrategiesFilterBar's
    // <select> and StrategiesTableView's header icons via
    // getEffectiveSortField, so applied order and displayed state always
    // agree. Reverses automatically once SHOW_STRATEGY_PERFORMANCE flips
    // back to true.
    const effectiveSortField = getEffectiveSortField(store.sortField);

    filtered.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;
      switch (effectiveSortField) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "lifetimeReturn":
          aVal = a.lifetimeReturn;
          bVal = b.lifetimeReturn;
          break;
        case "yearReturn":
          aVal = a.yearReturn;
          bVal = b.yearReturn;
          break;
        case "monthReturn":
          aVal = a.monthReturn;
          bVal = b.monthReturn;
          break;
        case "totalAum":
        default:
          aVal = a.totalAum;
          bVal = b.totalAum;
      }
      if (store.sortOrder === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }, [apiStrategies, store.filters, store.sortField, store.sortOrder]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    trackEvent("Page Viewed", { page_name: "strategies" });
  }, []);

  const handleAllocate = (strategyId: string) => {
    store.openAllocationModal(strategyId);
    trackEvent("Allocation Modal Opened", { strategy_id: strategyId });
  };

  const handleManage = (strategyId: string) => {
    store.openManageModal(strategyId);
    trackEvent("Manage Modal Opened", { strategy_id: strategyId });
  };

  if (!mounted) {
    return <StrategiesLoadingSkeleton />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-white">
      <StrategiesHeader />
      <PortfolioBuilderCTA />
      <StrategiesFilterBar />

      {/* API Error State */}
      {apiError && (
        <div className="my-6 rounded-lg border border-rose-500/50 bg-rose-500/10 p-4 text-rose-400">
          <p className="font-semibold">Failed to load strategies</p>
          <p className="mt-1 text-sm">{apiError.message}</p>
        </div>
      )}

      <StrategiesList
        strategies={strategies}
        isLoading={isLoading}
        onAllocate={handleAllocate}
        onManage={handleManage}
        isViewOnly={isViewOnly}
      />

      <PerformanceDisclaimer />

      {/* Modals */}
      <AllocationModal
        isOpen={store.allocationModalOpen}
        strategyId={store.allocationModalStrategyId}
        onClose={store.closeAllocationModal}
      />
      <ManageAllocationModal
        isOpen={store.manageModalOpen}
        strategyId={store.manageModalStrategyId}
        onClose={store.closeManageModal}
      />
    </div>
  );
}

const StrategiesLoadingSkeleton = () => (
  <div className="mx-auto max-w-6xl px-4 py-8">
    <div className="h-8 w-48 animate-pulse rounded bg-slate-700" />
    <div className="mt-2 h-5 w-96 animate-pulse rounded bg-slate-800" />
    <div className="mt-6 flex gap-3">
      <div className="h-10 w-32 animate-pulse rounded bg-slate-800" />
      <div className="h-10 w-32 animate-pulse rounded bg-slate-800" />
      <div className="ml-auto h-10 w-40 animate-pulse rounded bg-slate-800" />
    </div>
    <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-64 animate-pulse rounded-xl bg-slate-800" />
      ))}
    </div>
  </div>
);
